
import React, { useEffect , useState } from 'react';
import { CheckCircle, Home } from 'lucide-react';
import { createStripeAccountLink , addUserAsProviderManager , inviteUserAsProviderResource , getProviderManagerAndResource } from '../../../services/staffingService';
import { Redirect , Link , useParams } from 'react-router-dom';


import { useUser } from '../../../context/UserContext';
import { useProviderCompany } from '../../../context/ProviderCompanyContext';

import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';


const providerManagerSchema = yup.object({
    providerManagerEmail: yup.string()
        .required('Email is required for Provider Manager')
        .email('Must be a valid email address')
});

const providerResourceSchema = yup.object({
    providerResourceEmail: yup.string()
        .required('Email is required for Provider Resource')
        .email('Must be a valid email address')
});


const ProviderStaffAllocationCompanyOnboarding = () => {
    
    const { userInfo, userInfoReady } = useUser();
    const { providerCompanyId } = useParams();
    const { providerCompanyData, fetchProviderCompanyData } = useProviderCompany();
    const [loading, setLoading]= useState(true);

    const [managerResponse, setManagerResponse] = useState('');
    const [managerError, setManagerError] = useState(false);

    const [resourceResponse, setResourceResponse] = useState('');
    const [resourceError, setResourceError] = useState(false);

    const [totalProviderManagers, setTotalProviderManagers] = useState(0);
    const [totalProviderResources, setTotalProviderResources] = useState(0);

    const [progress, setProgress] = useState(0);

    // Separate useForm instances for each form
    const {
        register: registerManager,
        handleSubmit: handleSubmitManager,
        formState: { errors: errorsManager },
        reset: resetManager,
    } = useForm({
        resolver: yupResolver(providerManagerSchema),
    });

    const {
        register: registerResource,
        handleSubmit: handleSubmitResource,
        formState: { errors: errorsResource },
        reset: resetResource,
    } = useForm({
        resolver: yupResolver(providerResourceSchema),
    });

    const onSubmitProviderManager = async (data) => {
        try {
            const resp = await addUserAsProviderManager(providerCompanyId, data.providerManagerEmail);
            setManagerResponse(resp.data);
            setManagerError(false);
        } catch (error) {
            setResourceResponse(error.response.data || 'Failed to send invitation');
            setResourceError(true);
        }
    };

    const onSubmitProviderResource = async (data) => {
        try {
            const resp = await inviteUserAsProviderResource(providerCompanyId, data.providerResourceEmail);
            setResourceResponse(resp.data);
            setResourceError(false);
            
        } catch (error) {
            setResourceResponse(error.response.data || 'Failed to send invitation');
            setResourceError(true);
        }
    };


    useEffect(() => {
        if (providerCompanyId) {
            fetchProviderCompanyData(providerCompanyId);
            getProviderManagerAndResourceData(); 
            // setProgress(calculateProgress(providerCompanyData, totalProviderManagers, totalProviderResources ));
            setLoading(false);

        }
    }, [providerCompanyId, fetchProviderCompanyData]);

    useEffect(() => {
        if (providerCompanyData) {
            setProgress(calculateProgress(providerCompanyData, totalProviderManagers, totalProviderResources));
        }
    }, [providerCompanyData, totalProviderManagers, totalProviderResources]);


    const stripeIntegration = async () => {
        setLoading(true);
        try {
            const resp = await createStripeAccountLink(providerCompanyId);
            // setLoading(false);  
            window.location.href = resp.data.accountLink;
        } catch (error) {
            setLoading(false);  
            console.error("Failed to create Stripe account link:", error);
        }
    };

    // get providerManager 
    // get providerResource

    const getProviderManagerAndResourceData = async () => {
        const resp = await getProviderManagerAndResource(providerCompanyId);
        setTotalProviderManagers(resp.data.providerManagers);
        setTotalProviderResources(resp.data.providerResources);
    }

    
    const calculateProgress = (providerCompanyData, totalProviderManagers, totalProviderResources ) => {
        let progress = 0;
    
        // Stripe Onboarding
        if (providerCompanyData.stripeAccount.charges_enabled && 
            providerCompanyData.stripeAccount.details_submitted && 
            providerCompanyData.stripeAccount.payouts_enabled) {
            progress += 30;
        } else if (!providerCompanyData.stripeAccount.email) {
            progress += 10;
        } else if (providerCompanyData.stripeAccount.email && !providerCompanyData.stripeAccount.details_submitted) {
            progress += 20;
        }
    
        // Manager Onboarding
        if (totalProviderManagers < 2) {
            progress += 10;
        } else if (totalProviderManagers >= 2) {
            progress += 20;
        }
    
        // Security Personnel Onboarding
        if (totalProviderResources < 1) {
            progress += 10;
        } else if (totalProviderResources >= 1) {
            progress += 20;
        }
        return progress;
    };
    
    
    return (
        <>
        {
             providerCompanyData && <>
                <div className="bg-dashboard-gradient">
                    <div className="container mx-auto px-4 py-5 font-['DM_Sans']">
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="p-6">
                        <h5 className="text-base font-semibold text-[var(--brand-blue-dark)]">
                            { `Hello ${userInfo.firstName.charAt(0).toUpperCase() + userInfo.firstName.slice(1)}, please set up your account with ${providerCompanyData.stripeAccount.business_profile?.name.charAt(0).toUpperCase() + providerCompanyData.stripeAccount.business_profile?.name.slice(1)} to efficiently manage work orders.` }
                        </h5>
                        <div className="bg-[#e0e0e0] h-2 rounded-full mt-3 mb-2">
                            <div className="bg-[#13A391] h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                        </div>

                        {/* ========= stripe onboarding ======= */}


                        {
                            // Full verification: charges enabled, details submitted, and payouts enabled
                            providerCompanyData.stripeAccount.charges_enabled && 
                            providerCompanyData.stripeAccount.details_submitted && 
                            providerCompanyData.stripeAccount.payouts_enabled &&
                            <div className="flex items-start mt-4">
                                <CheckCircle className="text-[#13A391] text-xl mr-5" />
                                <div>
                                    <h6 className="mb-1 text-base font-semibold text-[var(--brand-blue-dark)]">
                                        Congratulations! Your Stripe account is verified and ready to receive payments and make payouts.
                                    </h6>
                                    <p className="mb-2 text-[var(--brand-gray-medium)] text-sm">
                                        Your account is fully verified and you can start transactions.
                                    </p>
                                    <button onClick={stripeIntegration} className="text-sm font-semibold text-[var(--brand-pink)] hover:underline">Go to Dashboard</button>
                                </div>
                            </div>
                        }

                        {
                            // No email provided yet
                            !providerCompanyData.stripeAccount.email &&
                            <div className="flex items-start mt-4">
                                <CheckCircle className="text-[#c9c4c1] text-xl mr-5" />
                                <div>
                                    <h6 className="mb-1 text-base font-semibold text-[var(--brand-blue-dark)]">
                                        Set Up Stripe for Business Identity Verification and Payments
                                    </h6>
                                    <p className="mb-2 text-[var(--brand-gray-medium)] text-sm">
                                        Ensure your business's credibility and streamline your payment processes by configuring Stripe for identity verification and secure financial transactions.
                                    </p>
                                    <button onClick={stripeIntegration} className="text-sm font-semibold text-[var(--brand-pink)] hover:underline">Start Onboarding</button>
                                </div>
                            </div>
                        }

                        {
                            // Email provided but details not submitted
                            providerCompanyData.stripeAccount.email && 
                            !providerCompanyData.stripeAccount.details_submitted &&
                            <div className="flex items-start mt-4">
                                <CheckCircle className="text-[#c9c4c1] text-xl mr-5" />
                                <div>
                                    <h6 className="mb-1 text-base font-semibold text-[var(--brand-blue-dark)]">
                                        Complete Your Stripe Setup
                                    </h6>
                                    <p className="mb-2 text-[var(--brand-gray-medium)] text-sm">
                                        Ensure your business's credibility and streamline your payment processes by configuring Stripe for identity verification and secure financial transactions.
                                    </p>
                                    <button onClick={stripeIntegration} className="text-sm font-semibold text-[var(--brand-pink)] hover:underline">Complete Verification</button>
                                </div>
                            </div>
                        }


                        {/* ========= add provider manager ======= */}

                        { totalProviderManagers < 2 && (
                            <div className="flex items-start mt-4">
                                <CheckCircle className="text-[#c9c4c1] text-xl mr-5" />
                                <div>
                                    <h6 className="mb-1 text-base font-semibold text-[var(--brand-blue-dark)]">Add Managers to Your Team</h6>
                                    <p className="mb-2 text-[var(--brand-gray-medium)] text-sm">
                                        Add managers to help allocate security staff for various work orders.
                                    </p>
                                    <form onSubmit={handleSubmitManager(onSubmitProviderManager)} className="mb-4">
                                        <div className="flex items-center mb-2">
                                            <input
                                                type="text"
                                                className="form-input w-[250px] h-[30px] text-sm mr-2"
                                                {...registerManager('providerManagerEmail')}
                                                placeholder="Add manager email"
                                            />
                                            <button type="submit" className="bg-[var(--brand-blue-dark)] text-white rounded-md w-6 h-6 flex items-center justify-center hover:bg-opacity-90">+</button>
                                        </div>
                                        {managerResponse && (
                                            <div className={`${managerError ? 'text-[var(--destructive)]' : 'text-[#13A391]'} text-sm`}>{managerResponse}</div>
                                        )}
                                        {errorsManager.providerManagerEmail && <div className="text-[var(--destructive)] text-sm">{errorsManager.providerManagerEmail.message}</div>}
                                    </form>
                                </div>
                            </div>
                        )}

                
                        { totalProviderManagers >= 2 && (
                            <div className="flex items-start mt-4">
                                <CheckCircle className="text-[#13A391] text-xl mr-5" />
                                <div>
                                    <h6 className="mb-1 text-base font-semibold text-[var(--brand-blue-dark)]">Manage Your Team</h6>
                                    <p className="mb-2 text-[var(--brand-gray-medium)] text-sm">
                                        Good job! You currently have {totalProviderManagers} {totalProviderManagers === 1 ? 'manager' : 'managers'} to help you manage work orders.
                                    </p>
                                    <form onSubmit={handleSubmitManager(onSubmitProviderManager)} className="mb-4">
                                        <div className="flex items-center mb-2">
                                            <input
                                                type="text"
                                                className="form-input w-[250px] h-[30px] text-sm mr-2"
                                                {...registerManager('providerManagerEmail')}
                                                placeholder="Add manager email"
                                            />
                                            <button type="submit" className="bg-[var(--brand-blue-dark)] text-white rounded-md w-6 h-6 flex items-center justify-center hover:bg-opacity-90">+</button>
                                        </div>
                                        {managerResponse && (
                                            <div className={`${managerError ? 'text-[var(--destructive)]' : 'text-[#13A391]'} text-sm`}>{managerResponse}</div>
                                        )}
                                        {errorsManager.providerManagerEmail && <div className="text-[var(--destructive)] text-sm">{errorsManager.providerManagerEmail.message}</div>}
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* ========= add provider resource ======= */}
            
                        { totalProviderResources < 1 && (
                            <div className="flex items-start mt-4">
                                <CheckCircle className="text-[#c9c4c1] text-xl mr-5" />
                                <div>
                                    <h6 className="mb-1 text-base font-semibold text-[var(--brand-blue-dark)]">Invite Security Personnel to Join Your Company</h6>
                                    <p className="mb-2 text-[var(--brand-gray-medium)] text-sm">
                                        Recruit security personnel to handle security assignments and operations.
                                    </p>
                                    <form onSubmit={handleSubmitResource(onSubmitProviderResource)} className="mb-4">
                                        <div className="flex items-center mb-2">
                                            <input
                                                type="text"
                                                className="form-input w-[250px] h-[30px] text-sm mr-2"
                                                {...registerResource('providerResourceEmail')}
                                                placeholder="Add security personnel email"
                                            />
                                            <button type="submit" className="bg-[var(--brand-blue-dark)] text-white rounded-md w-6 h-6 flex items-center justify-center hover:bg-opacity-90">+</button>
                                        </div>
                                        {resourceResponse && (
                                            <div className={`${resourceError ? 'text-[var(--destructive)]' : 'text-[#13A391]'} text-sm`}>{resourceResponse}</div>
                                        )}
                                        {errorsResource.providerResourceEmail && <div className="text-[var(--destructive)] text-sm">{errorsResource.providerResourceEmail.message}</div>}
                                    </form>
                                </div>
                            </div>
                        )}

                        { totalProviderResources >= 1 && (
                            <div className="flex items-start mt-4">
                                <CheckCircle className="text-[#13A391] text-xl mr-5" />
                                <div>
                                    <h6 className="mb-1 text-base font-semibold text-[var(--brand-blue-dark)]">Manage Your Security Personnel</h6>
                                    <p className="mb-2 text-[var(--brand-gray-medium)] text-sm">
                                        You currently have {totalProviderResources} {totalProviderResources === 1 ? 'security personnel' : 'security personnel'} in your team.
                                    </p>
                                    <form onSubmit={handleSubmitResource(onSubmitProviderResource)} className="mb-4">
                                        <div className="flex items-center mb-2">
                                            <input
                                                type="text"
                                                className="form-input w-[250px] h-[30px] text-sm mr-2"
                                                {...registerResource('providerResourceEmail')}
                                                placeholder="Add security personnel email"
                                            />
                                            <button type="submit" className="bg-[var(--brand-blue-dark)] text-white rounded-md w-6 h-6 flex items-center justify-center hover:bg-opacity-90">+</button>
                                        </div>
                                        {resourceResponse && (
                                            <div className={`${resourceError ? 'text-[var(--destructive)]' : 'text-[#13A391]'} text-sm`}>{resourceResponse}</div>
                                        )}
                                        {errorsResource.providerResourceEmail && <div className="text-[var(--destructive)] text-sm">{errorsResource.providerResourceEmail.message}</div>}
                                    </form>
                                </div>
                            </div>
                        )}
                        
                        {/* ========= start managing work orders ======= */}

                        <div className="flex items-start mt-4">
                            <span className="text-xl mr-5"> {'\u{1F389}'} </span>
                            <div>
                                <h6 className="mb-1 text-base font-semibold text-[var(--brand-blue-dark)]"> Start Managing Work Orders </h6>
                                <p className="mb-2 text-[var(--brand-gray-medium)] text-sm">    
                                    { progress === 100 ? 'Congratulations! You have completed all the tasks.' : 'Finish above tasks to start managing work orders.' }
                                </p>
                                <Link to={`/provider-company/${providerCompanyId}/jobs`} className="inline-block"> 
                                    <button className="text-sm font-semibold text-[var(--brand-pink)] hover:underline"> Go </button> 
                                </Link>
                            </div>
                        </div>
            
                        <div className="text-center mt-4">
                            <div className="mx-auto w-[100px]">
                                <Link to={`/home`} className="text-[var(--brand-blue-dark)] hover:text-[var(--brand-pink)]">
                                    <Home />
                                </Link>
                            </div>
                        </div>
            
                        </div>
                    </div>
                    </div>
                </div>
            </>
        }
        {
           loading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--brand-blue-dark)]"></div>
                </div>
           )
        }
        </>
    );
};

export default ProviderStaffAllocationCompanyOnboarding;
